import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Card, Flex, Text, Link, toast } from "@jsq/ds/core";
import { BaseDocumentType } from "../../graphql/supergraphOperations";
import useDocumentProcessor from "../../hooks/documents/useDocumentProcessor";
import { getErrorMessage } from "../../utils/error_parser";
import { textColorNeutral800 } from "../common/style_vanilla.css";
import { DocumentCollector } from "../documents/DocumentCollector";
import { DocumentData } from "../documents/DocumentCollector/types";
import { getDocumentCategoryInfo } from "../documents/DocumentCollector/utils";
import { mainContentWrapper } from "../style_vanilla.css";
import { titleStyle } from "./style_vanilla.css";
import { IndividualLandingProps } from "./types";

/**
 * This component provides an interface for users to upload identification documents
 * as required by regulatory requirements. It handles file validation, upload to S3,
 * and document registration in the system.
 *
 * The component will:
 * 1. Allow a user to upload identification documents
 * 3. Upload the document to S3
 * 4. Register the document in the system
 */
export const IndividualLandingScreen: React.FC<IndividualLandingProps> = ({
  caseId,
  profileId,
  basePath,
  showLinkToIraSelector,
}) => {
  const history = useHistory();
  const documentCategory = getDocumentCategoryInfo(BaseDocumentType.Id);
  const { processDocuments, loading } = useDocumentProcessor(caseId, profileId);

  const handleDocumentProcessing = useCallback(
    (replacedFiles: DocumentData[], newUploadFiles: DocumentData[]) => {
      processDocuments(replacedFiles, newUploadFiles)
        .then(() => {
          toast.success("Documents updated successfully");
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        });
    },
    [processDocuments],
  );
  return (
    <Card className={mainContentWrapper}>
      <Flex flexDirection="column" gap="20px">
        {showLinkToIraSelector && (
          <Link
            to={`${basePath}/ira-categorization`}
            data-testid="beneficial-owner-selection-back-link"
            variant="muted"
            icon="arrow-left"
            iconPosition="left"
            align="left"
          >
            Select account type
          </Link>
        )}
        <Text variant="h3" className={titleStyle}>
          Identification
        </Text>
        <Text
          variant="regular-md"
          className={textColorNeutral800}
        >{`Due to regulatory requirements, the following documents are required. You can skip this step for now if you don't have these documents available. You will need to return and upload any required documents before signing this subscription.`}</Text>
        <DocumentCollector
          isSubmitting={loading}
          documentDetails={[
            {
              baseType: BaseDocumentType.Id,
              subType: null,
              file: null,
              title: documentCategory.title,
              description: documentCategory.description,
            },
          ]}
          onComplete={handleDocumentProcessing}
          onNavigateBack={() => {
            history.goBack();
          }}
        />
      </Flex>
    </Card>
  );
};
